import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Drawer, List } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useBrandingStore } from '@/stores/brandingStore';
import { buildThemeFromBranding, darkSurfaces } from '@/utils/themeBuilder';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import osBuddyIconLight from '@/assets/os-buddy-icon-light.svg';
import osBuddyLogoLight from '@/assets/os-buddy-logo-light.svg';
import { NavItemRow, NavList } from './NavList';
import { isNavItemVisible, settingsNavItem } from './navigationModel';
import { AppVersionButton } from '@/features/releases/AppVersionButton';

export {
  isNavItemVisible,
  flattenNavItems,
  filterVisibleNavEntries,
  isNavGroup,
  navigationEntries,
  settingsNavItem,
  navItems,
  type NavItem,
  type NavGroup,
  type NavEntry,
  type TenantNavModules,
} from './navigationModel';

export const SIDEBAR_WIDTH = 205;
export const SIDEBAR_COLLAPSED_WIDTH = 80;

// Nav-icon layout constants — used both to lay out the nav items and to place the
// floating expand/collapse toggle on the exact same x-line as the icons.
export const SIDEBAR_LIST_PAD_X = 8; // List px:1
export const NAV_BUTTON_PAD_X = 16; // ListItemButton px:2 (expanded)
export const NAV_ICON_SLOT = 40; // ListItemIcon width
export const TOGGLE_SIZE = 28;

/** X-coordinate (px from the sidebar's left edge) of the nav-icon center line. */
export const getSidebarIconCenterX = (collapsed: boolean): number =>
  collapsed
    ? SIDEBAR_COLLAPSED_WIDTH / 2
    : SIDEBAR_LIST_PAD_X + NAV_BUTTON_PAD_X + NAV_ICON_SLOT / 2;

export function Sidebar() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const { inspectionsEnabled } = useTenantSettings();
  const brandingConfig = useBrandingStore((s) => s.branding?.branding) ?? null;
  const logoAlt = brandingConfig?.companyName ?? t('logoAlt');

  // The sidebar is always a dark icon rail (Figma "Oceanspace 0.2"),
  // independent of the app theme mode — like the login card.
  const darkTheme = useMemo(() => buildThemeFromBranding(brandingConfig, 'dark'), [brandingConfig]);

  const settingsVisible = isNavItemVisible(settingsNavItem, hasAnyPermission, {
    inspectionsEnabled,
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <Drawer
        variant="permanent"
        sx={{
          width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            // Sidebar chrome is the darkest surface per design system
            backgroundColor: darkSurfaces.sidebar,
            borderRight: 'none',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
          },
        }}
      >
        {/* Logo — OS Buddy brand (icon when collapsed, full wordmark when expanded).
            Fixed 64px height. The top divider AND the expand/collapse toggle are
            rendered once in AppShell as fixed elements, anchored to the same
            layout coordinates — not with a per-component border here. */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.5,
            height: 64,
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {/* Clicking the logo navigates to the dashboard */}
          <Box
            component="button"
            type="button"
            onClick={() => {
              navigateAwayFromWorkOrderDetail(location.pathname, '/', navigate);
            }}
            aria-label={t('dashboard')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              border: 0,
              p: 0,
              background: 'none',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={sidebarCollapsed ? osBuddyIconLight : osBuddyLogoLight}
              alt={logoAlt}
              // Height-driven so the icon and the full wordmark render at the same
              // visual scale (no size jump when toggling collapse).
              sx={{ height: 36, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        </Box>

        {/* Navigation — the only part that scrolls. Without minHeight: 0 the flex
            item grows to its content and the whole drawer paper scrolls instead,
            taking the logo and the pinned footer with it. */}
        <List
          sx={{ px: 1, py: 2, flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          <NavList variant="rail" collapsed={sidebarCollapsed} />
        </List>

        {/* Settings — pinned above the product brand. Help and support live in the user menu. */}
        {settingsVisible && (
          <List
            sx={{
              px: 1,
              py: 1,
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <NavItemRow item={settingsNavItem} variant="rail" collapsed={sidebarCollapsed} />
          </List>
        )}

        {/* OS Buddy product brand + app version — icon when collapsed, full wordmark when expanded */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            px: sidebarCollapsed ? 0 : 2.5,
            py: 1.5,
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {sidebarCollapsed ? (
            <Box
              component="img"
              src={osBuddyIconLight}
              alt={logoAlt}
              sx={{ width: 44, height: 'auto' }}
            />
          ) : (
            <Box
              component="img"
              src={osBuddyLogoLight}
              alt={logoAlt}
              sx={{ width: '100%', maxWidth: 160, height: 'auto' }}
            />
          )}
          <AppVersionButton />
        </Box>
      </Drawer>
    </ThemeProvider>
  );
}
